// Centralized measurement formatting that honors the user's measurement-system
// preference (MeasurementUnit.Metric / .Imperial). All values are STORED in
// metric base units (meters) — convert only at display time via these helpers.
// Pair with the useMeasurementUnit() hook to read the current unit in a
// component: `formatDistance(meters, useMeasurementUnit())`.

import { MeasurementUnit } from '@/types/settings'

const METERS_PER_FOOT = 0.3048
const METERS_PER_MILE = 1609.344
const METERS_PER_KM = 1000

export const isMetric = (unit: MeasurementUnit): boolean => unit === MeasurementUnit.Metric

export const metersToFeet = (meters: number): number => meters / METERS_PER_FOOT
export const feetToMeters = (feet: number): number => feet * METERS_PER_FOOT
export const metersToMiles = (meters: number): number => meters / METERS_PER_MILE
export const milesToMeters = (miles: number): number => miles * METERS_PER_MILE

/**
 * Human-friendly distance from a value in METERS.
 * Metric: m (<1 km) then km. Imperial: ft (<1000 ft) then mi.
 */
export function formatDistance(meters: number, unit: MeasurementUnit): string {
  if (!Number.isFinite(meters)) return isMetric(unit) ? '0 m' : '0 ft'
  if (isMetric(unit)) {
    return meters < METERS_PER_KM
      ? `${Math.round(meters)} m`
      : `${(meters / METERS_PER_KM).toFixed(1)} km`
  }
  const feet = metersToFeet(meters)
  return feet < 1000 ? `${Math.round(feet)} ft` : `${metersToMiles(meters).toFixed(1)} mi`
}

/**
 * Human-friendly distance already expressed in MILES (some APIs return miles).
 * Converts to the user's unit.
 */
export function formatDistanceFromMiles(miles: number, unit: MeasurementUnit): string {
  return formatDistance(milesToMeters(miles), unit)
}

/**
 * Long-distance total — always the large unit, never feet or meters.
 *
 * A lifetime travel figure wants a thousands separator and no unit switching: "1,568 mi", not
 * the same number rendered in feet because a threshold said so.
 */
export function formatLongDistance(meters: number, unit: MeasurementUnit): string {
  if (!Number.isFinite(meters) || meters <= 0) return isMetric(unit) ? '0 km' : '0 mi'
  const value = isMetric(unit) ? meters / METERS_PER_KM : metersToMiles(meters)
  const rounded = value >= 100 ? Math.round(value) : Number(value.toFixed(1))
  return `${rounded.toLocaleString()} ${isMetric(unit) ? 'km' : 'mi'}`
}

/**
 * Elevation/altitude from a value in METERS. Always small units (m or ft) —
 * never km/mi.
 */
export function formatElevation(meters: number, unit: MeasurementUnit): string {
  if (!Number.isFinite(meters)) return isMetric(unit) ? '0 m' : '0 ft'
  return isMetric(unit) ? `${Math.round(meters)} m` : `${Math.round(metersToFeet(meters))} ft`
}

/**
 * Human-friendly ground speed from a value in METERS PER SECOND.
 * Metric: km/h. Imperial: mph.
 */
export function formatSpeed(metersPerSecond: number, unit: MeasurementUnit): string {
  if (!Number.isFinite(metersPerSecond)) return isMetric(unit) ? '0.0 km/h' : '0.0 mph'
  return isMetric(unit)
    ? `${(metersPerSecond * 3.6).toFixed(1)} km/h`
    : `${(metersPerSecond / METERS_PER_MILE * 3600).toFixed(1)} mph`
}
